import { create } from 'zustand';
import { persist }  from 'zustand/middleware';

import type { UserDto, TenantSummary, CompanyDto } from '@/types/api';

interface AppState {
  // Auth
  token:         string | null;
  refreshToken:  string | null;
  user:          UserDto | null;
  // Tenants
  tenants:        TenantSummary[];
  activeTenantId: string | null;
  // Companies
  companies:      CompanyDto[];
  activeCompanyId: string | null;
  // Indicates companies have been fetched at least once (transient, not persisted)
  companiesLoaded: boolean;
  // Global period context (YYYYMM)
  activePeriod:   string;
  // UI
  sidebarOpen:    boolean;
  theme:          'light' | 'dark' | 'system';

  // Onboarding — persisted so first-time users land on /onboarding
  onboardingCompleted: boolean;

  // Setup prompts (company / whatsapp) — persisted so dismiss survives page reload
  dismissedSetupPrompt: boolean;

  // Logout reason — set before logout to show a message on the login page
  logoutReason:     string | null;

  // true solo si esta sesión se autenticó vía el redirect SSO de ecocont_ui
  // (nunca en login nativo) — controla si se muestra "Volver al ERP", ya que
  // hay usuarios con acceso a este sistema pero no al ERP general.
  ssoOrigin:        boolean;

  // Actions
  setAuth:          (token: string, user: UserDto) => void;
  startNewSession:  (token: string, user: UserDto) => void;
  setRefreshToken:  (refreshToken: string) => void;
  setSsoOrigin:     (v: boolean) => void;
  setOnboardingCompleted: (v: boolean) => void;
  logout:           () => void;
  setLogoutReason:  (reason: string | null) => void;
  setTenants:       (tenants: TenantSummary[]) => void;
  setActiveTenant:  (id: string) => void;
  setCompanies:     (companies: CompanyDto[]) => void;
  setActiveCompany: (id: string) => void;
  setActivePeriod:  (period: string) => void;
  setSidebarOpen:   (open: boolean) => void;
  toggleSidebar:    () => void;
  setTheme:         (theme: 'light' | 'dark' | 'system') => void;
  dismissSetupPrompt: () => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      token:               null,
      refreshToken:        null,
      user:                null,
      logoutReason:        null,
      ssoOrigin:           false,
      onboardingCompleted: false,
      dismissedSetupPrompt: false,
      tenants:        [],
      activeTenantId: null,
      companies:      [],
      activeCompanyId: null,
      companiesLoaded: false,
      activePeriod:   (() => {
        const n = new Date();
        return `${n.getFullYear()}${String(n.getMonth() + 1).padStart(2, '0')}`;
      })(),
      sidebarOpen:    true,
      theme:          'light',

      setAuth: (token, user) => set({ token, user }),

      // Login real de una cuenta nueva (nativo o SSO) — a diferencia de
      // `setAuth` (que también se usa para refrescar el access token o
      // actualizar el perfil DENTRO de la misma sesión, y por eso no puede
      // tocar tenant/company), esta acción resetea todo el contexto de
      // tenant/empresa heredado de una sesión anterior en la misma pestaña.
      // Sin esto, loguear la cuenta B después de la A (sin pasar por
      // `logout()`) dejaba `tenants`/`companies`/`activeTenantId`/
      // `activeCompanyId` de A pintados en pantalla hasta que las queries de
      // B resolvieran (o para siempre, si B no tenía acceso al tenant de A).
      startNewSession: (token, user) => set({
        token, user,
        refreshToken:         null,
        ssoOrigin:            false,
        tenants:              [],
        activeTenantId:       null,
        companies:            [],
        activeCompanyId:      null,
        companiesLoaded:      false,
        dismissedSetupPrompt: false,
        logoutReason:         null,
      }),

      setRefreshToken: (refreshToken) => set({ refreshToken }),
      setSsoOrigin: (v) => set({ ssoOrigin: v }),

      logout: () => set({
        token:                null,
        refreshToken:         null,
        user:                 null,
        ssoOrigin:            false,
        tenants:              [],
        activeTenantId:       null,
        companies:            [],
        activeCompanyId:      null,
        companiesLoaded:      false,
        dismissedSetupPrompt: false,
      }),

      setLogoutReason:         (reason) => set({ logoutReason: reason }),
      setOnboardingCompleted:  (v)      => set({ onboardingCompleted: v }),

      setTenants: (tenants) => set(s => {
        // Valida que el ID guardado exista en la lista real; si no, usar el primero
        const validId = tenants.find(t => t.id === s.activeTenantId)?.id
          ?? tenants[0]?.id
          ?? null;
        return { tenants, activeTenantId: validId };
      }),

      setActiveTenant: (id) => set({ activeTenantId: id, companies: [], activeCompanyId: null }),

      setCompanies: (companies) => set(s => {
        // Si el ID guardado no existe en la lista nueva, usar la primera empresa
        const validId = companies.find(c => c.id === s.activeCompanyId)?.id
          ?? companies[0]?.id
          ?? null;
        return { companies, activeCompanyId: validId, companiesLoaded: true };
      }),

      dismissSetupPrompt: () => set({ dismissedSetupPrompt: true }),

      setActiveCompany: (id) => set({ activeCompanyId: id }),
      setActivePeriod:  (period) => set({ activePeriod: period }),

      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      toggleSidebar:  ()     => set(s => ({ sidebarOpen: !s.sidebarOpen })),

      setTheme: (theme) => {
        set({ theme });
        const root = document.documentElement;
        root.classList.remove('light', 'dark');
        if (theme === 'system') {
          const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          root.classList.add(prefersDark ? 'dark' : 'light');
        } else {
          root.classList.add(theme);
        }
      },
    }),
    {
      name:    'ecocont-app',
      partialize: s => ({
        token:                s.token,
        refreshToken:         s.refreshToken,
        user:                 s.user,
        ssoOrigin:            s.ssoOrigin,
        activeTenantId:       s.activeTenantId,
        activeCompanyId:      s.activeCompanyId,
        activePeriod:         s.activePeriod,
        theme:                s.theme,
        sidebarOpen:          s.sidebarOpen,
        logoutReason:         s.logoutReason,
        // onboardingCompleted is intentionally NOT persisted: routing is based on
        // hasCompanies from the API (cross-device truth), not localStorage.
        dismissedSetupPrompt: s.dismissedSetupPrompt,
      }),
    },
  ),
);

// ── Selectores derivados ─────────────────────────────────────────────────────
//
// Zustand hace `state = Object.assign({}, state, nextState)` en cada set() —
// eso COPIA EL VALOR de cualquier getter definido dentro del state (no
// preserva el getter), así que un `get activeCompany() {...}` dentro del
// store queda "congelado" con el valor que tenía en el primer set() de la
// app y nunca se vuelve a recalcular. Por eso estos derivados van como
// selectores externos — `useAppStore(selectActiveCompany)` sí se re-evalúa
// en cada notificación, porque corre contra el `getState()` vivo.
export const selectActiveTenant  = (s: AppState) => s.tenants.find(t => t.id === s.activeTenantId);
export const selectActiveCompany = (s: AppState) => s.companies.find(c => c.id === s.activeCompanyId);
